/**
 * Email + password — sign-in or sign-up in one form. Toggling between
 * the two doesn't change the layout; only the submit-button label
 * and the underlying Firebase call.
 *
 * Email + password specifically does NOT require Firebase's
 * authorized-domains entry to work, but we still host it on the auth
 * page so the partner integration is uniform across all four flows.
 *
 * Multi-tenant gotcha & the asymmetric fallback
 * ─────────────────────────────────────────────
 * Horus identities live in a single Firebase project shared across
 * every partner app that uses Horus-hosted auth. A user who has
 * already signed up via App A (or the Horus consumer wallet) will
 * try to "create an account" on partner App B — and Firebase will
 * reject with `auth/email-already-in-use`. From their point of view
 * they've never used App B, so a blunt error is confusing.
 *
 * Sign-up handler now does an asymmetric fallback: when create fails
 * with email-already-in-use, we transparently re-attempt as a
 * sign-in with the same credentials. If their password happens to
 * match their existing Horus password (the common case), they get
 * signed in seamlessly. If it doesn't match, we switch the form to
 * sign-in mode, preserve the email, and surface a clear actionable
 * message ("You already have a Horus account — enter your existing
 * password").
 *
 * The reverse direction (sign-in → user-not-found → auto-create) is
 * INTENTIONALLY NOT done. A user typo'ing their email on the sign-in
 * form would otherwise silently create a new account at the typo'd
 * address — much worse than telling them to use the Sign up toggle.
 * The asymmetry is by design: blocked sign-ups are recoverable, but
 * accidental sign-ups aren't.
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  UserCredential,
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { PostMessageContext } from '../postmessage';
import {
  el,
  mount,
  postCancel,
  postError,
  postReady,
  postSuccess,
  humanizeFirebaseError,
  setBusy,
  getQueryParam,
} from '../util';

type Mode = 'sign_in' | 'sign_up';

const errorCodeOf = (err: unknown): string | undefined => {
  if (err && typeof err === 'object' && 'code' in err) {
    const c = (err as { code: unknown }).code;
    if (typeof c === 'string') return c;
  }
  return undefined;
};

export function renderEmailPasswordFlow(ctx: PostMessageContext): void {
  let mode: Mode = 'sign_in';

  const titleEl = el('h1', {}, ['Sign in']);
  const subtitleEl = el('p', { class: 'subtitle' }, [
    'Use your Horus email and password.',
  ]);
  const errorBox = el('p', { 'data-error': 'true', class: 'error', style: 'display:none' });

  const emailInput = el('input', {
    type: 'email',
    autocomplete: 'email',
    placeholder: 'you@example.com',
    required: true,
  }) as HTMLInputElement;
  const prefilledEmail = getQueryParam('email');
  if (prefilledEmail) emailInput.value = prefilledEmail;

  const passwordInput = el('input', {
    type: 'password',
    autocomplete: 'current-password',
    placeholder: 'Password',
    required: true,
  }) as HTMLInputElement;

  const submit = el('button', { class: 'primary', type: 'submit' }, [
    'Sign in',
  ]) as HTMLButtonElement;

  const switchLink = el('button', {
    class: 'switch',
    type: 'button',
  }, ['Need an account? Sign up']) as HTMLButtonElement;

  const cancel = el('button', { type: 'button' }, ['Cancel']);
  cancel.addEventListener('click', () => postCancel(ctx));

  const setMode = (next: Mode) => {
    mode = next;
    if (mode === 'sign_up') {
      titleEl.textContent = 'Create your account';
      subtitleEl.textContent = 'Set up a Horus email and password.';
      submit.textContent = 'Create account';
      switchLink.textContent = 'Already have an account? Sign in';
      passwordInput.autocomplete = 'new-password';
    } else {
      titleEl.textContent = 'Sign in';
      subtitleEl.textContent = 'Use your Horus email and password.';
      submit.textContent = 'Sign in';
      switchLink.textContent = 'Need an account? Sign up';
      passwordInput.autocomplete = 'current-password';
    }
  };

  switchLink.addEventListener('click', () => {
    setMode(mode === 'sign_in' ? 'sign_up' : 'sign_in');
    // Clear any stale error when the user explicitly switches mode.
    errorBox.style.display = 'none';
    errorBox.textContent = '';
  });

  const showError = (msg: string) => {
    errorBox.textContent = msg;
    errorBox.style.display = 'block';
  };

  const form = el('form', {}, [
    el('label', { for: 'email' }, ['Email']),
    emailInput,
    el('label', { for: 'password' }, ['Password']),
    passwordInput,
    submit,
    cancel,
    switchLink,
  ]);

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    errorBox.style.display = 'none';
    errorBox.textContent = '';
    setBusy(true);
    const auth = getFirebaseAuth();
    const email = emailInput.value;
    const password = passwordInput.value;

    try {
      let result: UserCredential;

      if (mode === 'sign_in') {
        // Sign-in path: no auto-fallback to create-account (would
        // silently create an account if the user typo'd their email).
        // We DO improve the user-not-found message below so they
        // know to click Sign up.
        try {
          result = await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
          const code = errorCodeOf(err);
          if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
            // Firebase recently started returning `invalid-credential`
            // for unknown email AND wrong password (email-enumeration
            // protection). We can't tell them apart without an extra
            // probe round-trip; surface a message that covers both.
            showError(
              "We couldn't sign you in. If you're new here, click \"Need an account? Sign up\" below — otherwise check your email and password."
            );
            postError(ctx, code ?? 'auth/unknown', 'Sign-in failed.');
            return;
          }
          throw err;
        }
        await postSuccess(ctx, result.user, 'password');
        return;
      }

      // Sign-up path with the email-already-in-use fallback.
      try {
        result = await createUserWithEmailAndPassword(auth, email, password);
      } catch (createErr) {
        const code = errorCodeOf(createErr);
        if (code !== 'auth/email-already-in-use') {
          throw createErr; // surface other errors (weak-password, invalid-email, etc.) below.
        }

        // The email IS in the shared Horus identity pool — try the
        // same password as a sign-in. This rescues the common case
        // where the user genuinely re-uses their existing password
        // even though they thought they were "creating an account".
        try {
          result = await signInWithEmailAndPassword(auth, email, password);
        } catch (signInErr) {
          const signInCode = errorCodeOf(signInErr);
          if (
            signInCode === 'auth/wrong-password' ||
            signInCode === 'auth/invalid-credential' ||
            signInCode === 'auth/invalid-login-credentials'
          ) {
            // They have a Horus account but the password they tried
            // doesn't match. Switch the form to sign-in mode and
            // tell them what's going on. Preserve the email; clear
            // the password so they can try their real one.
            setMode('sign_in');
            passwordInput.value = '';
            passwordInput.focus();
            showError(
              'You already have a Horus account with this email. Sign in with your existing password to continue. (Your wallet on this app will still be brand-new — Horus identities are shared across apps, but each app gets its own wallet.)'
            );
            postError(
              ctx,
              'auth/email-already-in-use',
              'Account exists; awaiting correct password.'
            );
            return;
          }
          // Some other sign-in error (network, etc.) — surface raw.
          throw signInErr;
        }
        // Sign-in fallback succeeded.
        await postSuccess(ctx, result.user, 'password');
        return;
      }

      // Create-account succeeded.
      await postSuccess(ctx, result.user, 'password');
    } catch (err) {
      const { code, message } = humanizeFirebaseError(err);
      showError(message);
      postError(ctx, code, message);
    } finally {
      setBusy(false);
    }
  });

  mount(el('div', {}, [titleEl, subtitleEl, errorBox, form]));
  postReady(ctx);
}
